'use client';

import { AppBar, Container, type Theme, Toolbar, useTheme } from '@mui/material';
import { type ScreenType } from '@tmdb-graphql-api/schema/types';
import { usePathname } from 'next/navigation.js';
import { Suspense, useEffect, useState } from 'react';
import { ToolbarState } from '#enums/headerStates.ts';
import { Route } from '#enums/routes.ts';
import { DiscoverHeader } from '../DiscoverHeader/index.tsx';
import { DefaultBar } from './DefaultBar.tsx';
import { SearchBar } from './SearchBar.tsx';
import { SecondaryToolbar } from './styled.ts';

export type RenderSearchToolbarParams = {
  searchFullScreen?: boolean;
  setActiveToolbarState: (state?: ToolbarState) => void;
  setSearchFullScreen: (fullScreen: boolean) => void;
  theme: Theme;
};

const renderSearchToolbar = ({
  searchFullScreen,
  setActiveToolbarState,
  setSearchFullScreen,
  theme,
}: RenderSearchToolbarParams) => (
  <SearchBar
    searchFullScreen={searchFullScreen}
    setActiveToolbarState={setActiveToolbarState}
    setSearchFullScreen={setSearchFullScreen}
    sx={{
      [theme.breakpoints.up('sm')]: {
        marginBottom: theme.spacing(2),
      },
      marginBottom: theme.spacing(1),
    }}
  />
);

const toolbars = {
  search: renderSearchToolbar,
};

export type HeaderProps = {
  type?: ScreenType;
};

export const Header = ({ type }: HeaderProps) => {
  const pathname = usePathname();
  const theme = useTheme();
  const [searchFullScreen, setSearchFullScreen] = useState(false);
  const searchResultsRoute = Route.SearchResults.toString();
  const defaultToolbarState = pathname === searchResultsRoute ? ToolbarState.Search : undefined;
  const [activeToolbarState, setActiveToolbarState] = useState<ToolbarState | undefined>(defaultToolbarState);

  useEffect(() => {
    if (pathname !== searchResultsRoute && activeToolbarState === ToolbarState.Search) {
      setActiveToolbarState(undefined);
    } else if (pathname === searchResultsRoute && activeToolbarState !== ToolbarState.Search) {
      setActiveToolbarState(ToolbarState.Search);
    }
    // should only run on initial render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    document.body.style.overflow = searchFullScreen ? 'hidden' : 'visible';
  }, [searchFullScreen]);

  return (
    <AppBar
      color="primary"
      elevation={1}
      position="sticky"
      sx={{
        flexGrow: 1,
        height: searchFullScreen ? '100dvh' : undefined,
      }}
    >
      <Toolbar
        sx={{
          borderBottom: `1px solid ${theme.palette.grey[600]}`,
          flexDirection: 'column',
          height: searchFullScreen ? '100%' : undefined,
        }}
      >
        <DefaultBar
          activeToolbarState={activeToolbarState}
          searchFullScreen={searchFullScreen}
          setActiveToolbarState={setActiveToolbarState}
          setSearchFullScreen={setSearchFullScreen}
        />
        {activeToolbarState ? (
          <Suspense>
            {toolbars[activeToolbarState]({
              searchFullScreen,
              setActiveToolbarState,
              setSearchFullScreen: (value: boolean) => {
                setSearchFullScreen(value);
              },
              theme,
            })}
          </Suspense>
        ) : undefined}
      </Toolbar>
      {type && pathname.startsWith(Route.Discover) && !searchFullScreen ? (
        <SecondaryToolbar>
          <Container disableGutters maxWidth="xl" sx={{ paddingLeft: 0, paddingRight: 0 }}>
            <DiscoverHeader type={type} />
          </Container>
        </SecondaryToolbar>
      ) : undefined}
    </AppBar>
  );
};
